import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import SEOHead from "../components/SEOHead";
import { ScrollText } from "lucide-react";

const SECTIONS = [
  {
    id: "overview",
    label: "1. Overview",
    content: (
      <>
        <p>
          This Privacy Policy explains how{" "}
          <strong>
            Binshare Ltd (Company No. 16909547, registered in England &amp;
            Wales)
          </strong>{" "}
          — &quot;<strong>Binshare</strong>&quot;, &quot;we&quot;, &quot;us&quot; — collects and uses
          personal data when you use{" "}
          <a href="/" className="text-sage-700 underline underline-offset-2">
            binshare.co.uk
          </a>{" "}
          and the Binshare app (together, the <strong>Service</strong>). We
          try to keep this in plain English.
        </p>
        <p>
          For the purposes of the UK GDPR and the Data Protection Act 2018,
          Binshare is the <strong>data controller</strong> of personal
          information processed in connection with the Service. Our registered
          office is in Caernarfon, Gwynedd, United Kingdom.
        </p>
      </>
    ),
  },
  {
    id: "data-we-collect",
    label: "2. What data we collect",
    content: (
      <>
        <p>We collect the following categories of personal data:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account data</strong> — name, email, phone number, your
            hashed password, profile photo, postcode and role (customer or
            carrier).
          </li>
          <li>
            <strong>Verification data (carriers only)</strong> — company name,
            waste carrier registration number, insurance documents.
          </li>
          <li>
            <strong>Job data</strong> — photos of items, descriptions,
            postcodes, quotes, messages, ratings and reviews.
          </li>
          <li>
            <strong>Payment data</strong> — processed by our payment provider;
            we only receive limited information (last four digits of a card,
            payout status). We do not store full card numbers.
          </li>
          <li>
            <strong>Device &amp; usage data</strong> — IP address, browser
            type, device identifiers, pages visited, app interactions and
            crash logs.
          </li>
          <li>
            <strong>Communications</strong> — emails you send us, support
            tickets and marketing-preference choices.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    label: "3. How we use your data",
    content: (
      <>
        <p>We use personal data to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>run your account and provide the Service;</li>
          <li>
            match customers with verified carriers and share job details
            between the two parties;
          </li>
          <li>process payments and payouts;</li>
          <li>
            check carriers against the official waste carrier register for
            their area;
          </li>
          <li>
            keep the Service safe — prevent fraud, debug, and stop illegal
            disposal;
          </li>
          <li>
            send essential service emails and, with your consent, marketing
            updates about Binshare&apos;s features and new locations;
          </li>
          <li>comply with our legal obligations.</li>
        </ul>
      </>
    ),
  },
  {
    id: "legal-basis",
    label: "4. Legal bases (UK GDPR)",
    content: (
      <>
        <p>We rely on one or more of the following lawful bases:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Contract</strong> — to deliver the Service you signed up
            to.
          </li>
          <li>
            <strong>Legitimate interests</strong> — for security, fraud
            prevention, analytics and service improvement.
          </li>
          <li>
            <strong>Consent</strong> — for marketing emails and non-essential
            cookies. You can withdraw at any time.
          </li>
          <li>
            <strong>Legal obligation</strong> — for tax records and reporting
            illegal waste activity to UK regulators.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    label: "5. Who we share data with",
    content: (
      <>
        <p>
          We share personal data only as needed to run Binshare. We do not
          sell personal data. Recipients include:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Other users</strong> — for example, when you book a job we
            share your first name, message thread and the relevant postcode
            area with the carrier you booked.
          </li>
          <li>
            <strong>Service providers</strong> — hosting, database, email
            delivery, payment processing, error monitoring. They act as our
            data processors under written agreements.
          </li>
          <li>
            <strong>UK regulators &amp; law enforcement</strong> — where
            we&apos;re required to disclose, or to report suspected illegal
            waste activity (Environment Agency, councils, police).
          </li>
          <li>
            <strong>Professional advisers</strong> — lawyers, accountants and
            auditors, under confidentiality obligations.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "international",
    label: "6. International transfers",
    content: (
      <p>
        Our infrastructure is primarily hosted in the UK and the EU. Where
        personal data is transferred outside the UK (for example to a
        US-based email provider), we rely on UK GDPR-recognised safeguards
        such as the UK International Data Transfer Agreement, the EU Standard
        Contractual Clauses with UK Addendum, or adequacy decisions.
      </p>
    ),
  },
  {
    id: "retention",
    label: "7. How long we keep data",
    content: (
      <>
        <p>
          We keep personal data only for as long as necessary. Indicative
          periods:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Account data: while active, then 24 months.</li>
          <li>
            Job &amp; payout records: 6 years (UK tax-record retention).
          </li>
          <li>
            Duty-of-care waste-transfer records: at least 2 years (legal
            requirement).
          </li>
          <li>Support emails: up to 24 months after resolution.</li>
          <li>
            Server logs &amp; backups: typically up to 90 days, then deleted
            or anonymised.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "your-rights",
    label: "8. Your UK GDPR rights",
    content: (
      <>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>access a copy of your personal data;</li>
          <li>correct inaccurate data;</li>
          <li>
            request erasure (where it doesn&apos;t conflict with our legal
            duties);
          </li>
          <li>restrict or object to processing;</li>
          <li>ask for data portability for data you provided;</li>
          <li>withdraw consent at any time;</li>
          <li>
            complain to the UK Information Commissioner&apos;s Office (ICO) at{" "}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sage-700 underline underline-offset-2"
            >
              ico.org.uk
            </a>
            .
          </li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a
            href="mailto:support@binshare.co.uk"
            className="text-sage-700 underline underline-offset-2"
          >
            support@binshare.co.uk
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    label: "9. Cookies",
    content: (
      <p>
        Binshare uses essential and (with your consent) analytics cookies. See
        our{" "}
        <a
          href="/cookies"
          className="text-sage-700 underline underline-offset-2"
        >
          Cookie Policy
        </a>{" "}
        for the full list and how to manage them.
      </p>
    ),
  },
  {
    id: "security",
    label: "10. Security",
    content: (
      <p>
        We use industry-standard technical and organisational measures —
        TLS-encrypted connections, hashed passwords, access controls and
        regular backups — to protect personal data. No system is perfect;
        notify us immediately at{" "}
        <a
          href="mailto:support@binshare.co.uk"
          className="text-sage-700 underline underline-offset-2"
        >
          support@binshare.co.uk
        </a>{" "}
        if you suspect an incident affecting your account.
      </p>
    ),
  },
  {
    id: "children",
    label: "11. Children",
    content: (
      <p>
        Binshare isn&apos;t intended for children under 18 and we don&apos;t
        knowingly collect their data. If you believe a child has provided
        data, please contact us and we&apos;ll delete it.
      </p>
    ),
  },
  {
    id: "changes",
    label: "12. Changes to this policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time. We&apos;ll
        notify you of material changes by email or in-app and update the
        &quot;Last updated&quot; date above.
      </p>
    ),
  },
  {
    id: "contact",
    label: "13. Contact us",
    content: (
      <div className="rounded-2xl border border-moss-200/50 bg-cream-100/60 p-5 sm:p-6 not-prose">
        <p className="font-semibold text-moss-900">Binshare Ltd</p>
        <p className="text-sm text-moss-700 mt-1">
          Company No. 16909547 — Registered in England &amp; Wales
        </p>
        <p className="text-sm text-moss-700 mt-1">
          Registered office: Caernarfon, Gwynedd, United Kingdom
        </p>
        <p className="text-sm mt-3">
          Contact:{" "}
          <a
            href="mailto:support@binshare.co.uk"
            className="text-sage-700 underline underline-offset-2 font-medium"
            data-testid="privacy-contact-email"
          >
            support@binshare.co.uk
          </a>
        </p>
      </div>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <>
      <SEOHead
        path="/privacy"
        title="Privacy Policy | Binshare Waste Disposal Marketplace UK"
        description="How Binshare collects, uses and protects your personal data — plain-English UK GDPR privacy policy for the UK's waste disposal marketplace."
        keywords="Binshare privacy policy, UK GDPR waste disposal, Data Protection Act 2018, waste marketplace data protection"
      />
      <LegalLayout
        testidPrefix="privacy"
        eyebrow={
          <>
            <ScrollText className="w-3.5 h-3.5" /> Legal
          </>
        }
        title="Privacy Policy"
        intro="How Binshare collects, uses and protects your personal data — plain English, aligned with UK GDPR and the Data Protection Act 2018."
        effective="1 February 2026"
        lastUpdated="13 October 2026"
        sections={SECTIONS}
      />
    </>
  );
}
